/**
 * Stage 33 — React Query hooks for the candidate surface.
 *
 * Every hook is `enabled`-gated by `isCrossUnitEnabledClient()`. With the flag off React
 * Query never runs a query function, so the browser issues zero candidate requests — the
 * server proxy gate is the second, authoritative line of defence.
 *
 * Cluster alternatives and the pair list are paginated hooks by construction: there is no
 * "fetch all pairs" or "fetch all alternatives" hook to call by mistake.
 */
import { useQuery } from '@tanstack/react-query'
import * as api from './cross-unit.api'
import { isCrossUnitEnabledClient } from './cross-unit.flag'
import type {
  CandidateClusterPairSort,
  CandidatePairQuery,
} from './cross-unit.types'
import type { CrossUnitCasesQuery } from './cross-unit.user-view'

export const crossUnitKeys = {
  all: ['analytics', 'cross-unit'] as const,
  coverage: () => [...crossUnitKeys.all, 'coverage'] as const,
  pairs: (q: CandidatePairQuery) => [...crossUnitKeys.all, 'pairs', q] as const,
  pair: (id: string) => [...crossUnitKeys.all, 'pair', id] as const,
  scores: (id: string) => [...crossUnitKeys.all, 'scores', id] as const,
  explanations: (id: string) => [...crossUnitKeys.all, 'explanations', id] as const,
  pairCluster: (id: string) => [...crossUnitKeys.all, 'pair-cluster', id] as const,
  cluster: (gid: string) => [...crossUnitKeys.all, 'cluster', gid] as const,
  clusterPairs: (gid: string, page: number, pageSize: number, sort: string) =>
    [...crossUnitKeys.all, 'cluster-pairs', gid, page, pageSize, sort] as const,
  // Stage 36 — user-facing scenarios 6–7
  casesSummary: (q: CrossUnitCasesQuery) => [...crossUnitKeys.all, 'cases-summary', q] as const,
  casesList: (q: CrossUnitCasesQuery) => [...crossUnitKeys.all, 'cases-list', q] as const,
  eligibilityAudit: () => [...crossUnitKeys.all, 'eligibility-audit'] as const,
  // Stage 40 — rows behind one evidence number; cached per (pair, evidence key)
  evidenceExamples: (id: string, key: string) =>
    [...crossUnitKeys.all, 'evidence-examples', id, key] as const,
  // Stage 43 — one of those rows in full; cached per corpus record, not per case:
  // the same record reached from two different reasons is the same record.
  evidenceRow: (sourceUid: string) =>
    [...crossUnitKeys.all, 'evidence-row', sourceUid] as const,
}

const STALE = 5 * 60_000

export function useCandidateCoverage(enabled = true) {
  return useQuery({
    queryKey: crossUnitKeys.coverage(),
    queryFn: () => api.fetchCandidateCoverage(),
    enabled: isCrossUnitEnabledClient() && enabled,
    staleTime: STALE,
    retry: false,
  })
}

export function useCandidatePairs(q: CandidatePairQuery, enabled = true) {
  return useQuery({
    queryKey: crossUnitKeys.pairs(q),
    queryFn: () => api.fetchCandidatePairs(q),
    enabled: isCrossUnitEnabledClient() && enabled,
    staleTime: 60_000,
    retry: false,
  })
}

export function useCandidatePair(canonicalPairId: string | null, enabled = true) {
  return useQuery({
    queryKey: crossUnitKeys.pair(canonicalPairId ?? ''),
    queryFn: () => api.fetchCandidatePair(canonicalPairId as string),
    enabled: isCrossUnitEnabledClient() && enabled && !!canonicalPairId,
    staleTime: STALE,
    retry: false,
  })
}

export function useCandidatePairScores(canonicalPairId: string | null, enabled = true) {
  return useQuery({
    queryKey: crossUnitKeys.scores(canonicalPairId ?? ''),
    queryFn: () => api.fetchCandidatePairScores(canonicalPairId as string),
    enabled: isCrossUnitEnabledClient() && enabled && !!canonicalPairId,
    staleTime: STALE,
    retry: false,
  })
}

export function useCandidatePairExplanations(canonicalPairId: string | null, enabled = true) {
  return useQuery({
    queryKey: crossUnitKeys.explanations(canonicalPairId ?? ''),
    queryFn: () => api.fetchCandidatePairExplanations(canonicalPairId as string),
    enabled: isCrossUnitEnabledClient() && enabled && !!canonicalPairId,
    staleTime: STALE,
    retry: false,
  })
}

/** Cluster of one pair — lazy: only fetched when its section is opened. */
export function useCandidatePairCluster(canonicalPairId: string | null, enabled = false) {
  return useQuery({
    queryKey: crossUnitKeys.pairCluster(canonicalPairId ?? ''),
    queryFn: () => api.fetchCandidatePairCluster(canonicalPairId as string),
    enabled: isCrossUnitEnabledClient() && enabled && !!canonicalPairId,
    staleTime: STALE,
    retry: false,
  })
}

export function useCandidateCluster(stableCaseGroupId: string | null, enabled = true) {
  return useQuery({
    queryKey: crossUnitKeys.cluster(stableCaseGroupId ?? ''),
    queryFn: () => api.fetchCandidateCluster(stableCaseGroupId as string),
    enabled: isCrossUnitEnabledClient() && enabled && !!stableCaseGroupId,
    staleTime: STALE,
    retry: false,
  })
}

/** Alternatives — mandatory pagination; there is no unpaginated variant on purpose. */
export function useCandidateClusterPairs(
  stableCaseGroupId: string | null,
  page: number,
  pageSize: number,
  sort: CandidateClusterPairSort = 'track_a_desc',
  enabled = false,
) {
  return useQuery({
    queryKey: crossUnitKeys.clusterPairs(stableCaseGroupId ?? '', page, pageSize, sort),
    queryFn: () => api.fetchCandidateClusterPairs(stableCaseGroupId as string, { page, pageSize, sort }),
    enabled: isCrossUnitEnabledClient() && enabled && !!stableCaseGroupId,
    staleTime: 60_000,
    retry: false,
  })
}

/* ── Stage 36: user-facing scenarios 6–7 ──────────────────────────────────── */
// Summary and list take the SAME filter object, so the cards can never describe a
// different population than the table below them. Only the paging/sort keys differ.

export function useCrossUnitCasesSummary(q: CrossUnitCasesQuery, enabled = true) {
  return useQuery({
    queryKey: crossUnitKeys.casesSummary(q),
    queryFn: () => api.fetchCrossUnitCasesSummary(q),
    enabled: isCrossUnitEnabledClient() && enabled,
    staleTime: 60_000,
    retry: false,
  })
}

export function useCrossUnitCasesList(q: CrossUnitCasesQuery, enabled = true) {
  return useQuery({
    queryKey: crossUnitKeys.casesList(q),
    queryFn: () => api.fetchCrossUnitCasesList(q),
    enabled: isCrossUnitEnabledClient() && enabled,
    staleTime: 60_000,
    retry: false,
  })
}

/** Diagnostics only — the excluded population stays inspectable without a UI toggle. */
export function useCrossUnitEligibilityAudit(enabled = false) {
  return useQuery({
    queryKey: crossUnitKeys.eligibilityAudit(),
    queryFn: () => api.fetchCrossUnitEligibilityAudit(),
    enabled: isCrossUnitEnabledClient() && enabled,
    staleTime: 5 * 60_000,
    retry: false,
  })
}

/**
 * Stage 40 — historical rows behind one evidence number.
 *
 * `enabled` is false until the analyst opens the row, so the drawer's first render issues
 * no request at all. The result is cached under (pair, evidence key), so re-opening the
 * same row — or opening it again from a second reason card that quotes the same number —
 * does not re-query. React Query passes its AbortSignal through, so closing the drawer
 * mid-flight cancels the request instead of leaving it to land on an unmounted view.
 */
export function useEvidenceExamples(pairId: string | null, evidenceKey: string | null, enabled: boolean) {
  return useQuery({
    queryKey: crossUnitKeys.evidenceExamples(pairId ?? '', evidenceKey ?? ''),
    queryFn: ({ signal }) => api.fetchEvidenceExamples(pairId as string, evidenceKey as string, signal),
    enabled: !!pairId && !!evidenceKey && enabled && isCrossUnitEnabledClient(),
    staleTime: STALE,
    retry: 1,
  })
}

/**
 * Stage 43 — подробности одной строки примеров.
 *
 * `enabled` ложно, пока аналитик не открыл строку: таблица не делает ни одного запроса на
 * строку при отрисовке. Ключ — сам `source_uid`, поэтому повторное открытие той же записи
 * (в том числе из другой причины того же кейса) не запрашивает её заново.
 */
export function useEvidenceRowDetail(sourceUid: string | null) {
  return useQuery({
    queryKey: crossUnitKeys.evidenceRow(sourceUid ?? ''),
    queryFn: ({ signal }) => api.fetchEvidenceRowDetail(sourceUid as string, signal),
    enabled: !!sourceUid && isCrossUnitEnabledClient(),
    staleTime: STALE,
    retry: 1,
  })
}
